/**
 * Início — o guia de ativação, e a central de ações do dono depois dele.
 *
 * ## Por que guia, e não dashboard vazio
 *
 * A spec 04 é literal: *"a tela Início de um tenant sem canal não é um dashboard vazio com gráficos
 * zerados"*. O produto tem uma dependência dura na Meta e passos que o cliente **não controla**.
 * *"Um checklist honesto é o que separa 'está demorando, é assim mesmo, veja onde está' de 'não
 * funciona'."*
 *
 * ## O guia de TRÊS passos (spec 23, jornada 1 — aprovado pelo owner em 01/11)
 *
 * O Profissional tinha seis passos numa lista, com um cabeçalho dizendo "cinco", e três deles mandavam para a lista
 * genérica de Automações. Agora são três etapas com ícone e estado — **Ligar número → Quando ele responde →
 * Testar** —, cada uma juntando os passos que o servidor mede (`checklistDoProfissional`). A regra de "concluída"
 * continua a do servidor: a tela vira a central quando TODOS os passos fecham.
 *
 * ## Depois: a central de ações (D5, aprovada em 01/11)
 *
 * Até 01/11 o painel tinha "Onde ir agora" — um atalho por área, pedido do owner em 03/09. Ele repetia o menu
 * inteiro. O owner aprovou trocá-lo (D5) por duas coisas que o menu não diz: **"Precisam de você"**, com o VERBO que
 * resolve cada linha, e **"Equipe agora"** — quem está com quanta conversa, quem está sem fila ou fora da jornada.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CircleCheck,
  Clock,
  Gauge,
  Hourglass,
  LayoutDashboard,
  MessageCircleReply,
  MessageSquareWarning,
  QrCode,
  Smartphone,
  Timer,
  TrendingUp,
  TriangleAlert,
} from "lucide-react";
import {
  EstadoDaFalha,
  Ladrilho,
  Metricas,
  chamar,
  contar,
  falhaDe,
  identificadorLegivel,
  useSessao,
  type Metrica,
  type TomDoLadrilho,
} from "@hugi/ui";
import {
  CabecalhoDaTela,
  Carregando,
  GradeDeCartoes,
  PaginaInteira,
  SemPermissao,
  Selo,
  type CartaoDaGrade,
} from "@hugi/ui/kit";
import {
  duracaoCurta,
  estadoDaJornada,
  useAtivacao,
  useConexoes,
  useEquipe,
  useRelatorioDeVolume,
  useResumoDoInicio,
  type AtendenteNaLista,
  type Conexao,
  type ConsumoDoPlano,
  type PassoDaAtivacao,
  type ResumoDoAgente,
  type ResumoDoInicio,
} from "./dados.js";
import { canalDeConectar, PausarDoNumero } from "./acoes-do-dono.js";
import { fraseDaPunicao, nomeDaPunicao } from "./punicoes.js";
import "./hugi-inicio.css";

/*
 * O RESUMO COM OS CAMPOS NOVOS (contrato C2 da spec 23). Todos opcionais: o servidor que ainda não os manda deixa a
 * tela de pé — sem eles, a linha do assistente, do plano e do chamado simplesmente não aparece.
 */
export type ResumoComContratos = Omit<ResumoDoInicio, "esperando"> & {
  readonly esperando:
    | (NonNullable<ResumoDoInicio["esperando"]> & {
        /** A conversa mais antiga na fila — "Atender a mais antiga" abre ELA. Sem o campo, abre o atendimento. */
        readonly maisAntigaId?: string | null;
      })
    | null;
  readonly assistentes?: {
    readonly principal: {
      readonly id: string;
      readonly nome: string;
      readonly publicado: boolean;
      readonly atende: "nunca" | "fora_do_horario" | "sempre";
    } | null;
    readonly semPublicacao: readonly { readonly id: string; readonly nome: string; readonly recusas7d: number }[];
  } | null;
  readonly plano?: {
    readonly usadas: number;
    readonly franquia: number | null;
    readonly situacaoIa: string;
    readonly agenteParado: boolean;
  } | null;
  readonly chamadosAguardando?: number;
};

export function Inicio() {
  const sessao = useSessao();
  const ativacao = useAtivacao();

  if (ativacao.isPending) {
    return (
      <PaginaInteira rotulo="Início" classe="hugi-inicio">
        <Carregando />
      </PaginaInteira>
    );
  }
  if (ativacao.error) {
    return (
      <PaginaInteira rotulo="Início" classe="hugi-inicio">
        <EstadoDaFalha
          falha={falhaDe(ativacao.error) ?? { tipo: "inesperado", erro: { erro: "?" }, status: 0 }}
          aoRepetir={() => void ativacao.refetch()}
        />
      </PaginaInteira>
    );
  }

  const { passos, concluida } = ativacao.data!;
  const etapas = concluida ? null : etapasDoGuia(passos);

  return (
    <PaginaInteira rotulo="Início" classe="hugi-inicio">
      <CabecalhoDaTela
        icone={<LayoutDashboard size={16} />}
        titulo={concluida ? saudacao(sessao.usuario.nome) : `Vamos ativar o ${sessao.tenant.nome}`}
        recorte={concluida ? hojePorExtenso() : recorteDaAtivacao(passos, etapas)}
        primaria={
          concluida && sessao.permissoes.includes("read_report") ? (
            <Link href="/relatorios" className="v2-botao">
              <TrendingUp size={14} aria-hidden="true" /> Ver relatórios
            </Link>
          ) : undefined
        }
      />

      {concluida ? <PainelDeOperacao /> : etapas ? <Guia etapas={etapas} /> : <Checklist passos={passos} />}
    </PaginaInteira>
  );
}

/** "Bom dia, Ana" (spec 23): a tela de entrada cumprimenta, em vez de rotular. Sem nome (o introspect nem sempre traz), só o cumprimento. */
export function saudacao(nome: string | null | undefined, agora: Date = new Date()): string {
  const h = agora.getHours();
  const cumprimento = h < 12 ? "Bom dia" : h < 18 ? "Boa tarde" : "Boa noite";
  const primeiro = nome?.trim().split(/\s+/)[0];
  return primeiro ? `${cumprimento}, ${primeiro}` : cumprimento;
}

/** "Quinta, 3 de setembro" — a data no cabeçalho é o que separa "hoje" de "um retrato qualquer". */
function hojePorExtenso(): string {
  const s = new Intl.DateTimeFormat("pt-BR", { weekday: "long", day: "numeric", month: "long" }).format(new Date());
  const semFeira = s.replace("-feira", "");
  return semFeira.charAt(0).toUpperCase() + semFeira.slice(1);
}

const POR_EXTENSO = ["Nenhum", "Um", "Dois", "Três", "Quatro", "Cinco", "Seis", "Sete", "Oito", "Nove", "Dez"];

/**
 * O recorte do cabeçalho na ativação. A CONTAGEM vem do que a tela mostra — o "Cinco passos" fixo no texto mentia
 * para o Profissional, que tinha seis (spec 23). E "depende da Meta" só aparece quando algum passo depende.
 */
export function recorteDaAtivacao(passos: readonly PassoDaAtivacao[], etapas: readonly EtapaDoGuia[] | null): string {
  const n = etapas ? etapas.length : passos.length;
  const quantos = `${POR_EXTENSO[n] ?? String(n)} ${n === 1 ? "passo" : "passos"}.`;
  return passos.some((p) => p.dependeDeTerceiro && !p.concluido) ? `${quantos} Alguns dependem da Meta, e a lista diz quais.` : quantos;
}

// ---------------------------------------------------------------------------------------------
// O guia de três passos (Profissional)
// ---------------------------------------------------------------------------------------------

export interface EtapaDoGuia {
  readonly id: "ligar" | "quando" | "testar";
  readonly titulo: string;
  readonly passos: readonly PassoDaAtivacao[];
  readonly concluida: boolean;
  readonly estado: "feita" | "agora" | "depois";
}

const ETAPAS: readonly { id: EtapaDoGuia["id"]; titulo: string; passos: readonly string[] }[] = [
  { id: "ligar", titulo: "Ligar número", passos: ["conectar"] },
  // "Quando ele responde" junta escolher o horário e publicar: são as duas metades de "responde sozinho", e uma sem a
  // outra era a recusa calada das 142 respostas perdidas (spec 23, a raiz). O material entra aqui porque é revisado
  // na mesma tela do assistente.
  { id: "quando", titulo: "Quando ele responde", passos: ["material", "quando", "publicar"] },
  { id: "testar", titulo: "Testar", passos: ["conversa", "alerta"] },
];

/**
 * Os passos do servidor nas três etapas — ou `null` quando a checklist não é a do Profissional (a geral, de equipe,
 * continua lista). Passo que o servidor acrescentar e a tela não conhecer entra na última etapa: some não pode, senão
 * a ativação nunca fecha e ninguém vê por quê.
 */
export function etapasDoGuia(passos: readonly PassoDaAtivacao[]): EtapaDoGuia[] | null {
  if (!passos.some((p) => p.id === "conectar")) return null;
  const conhecidos = new Set(ETAPAS.flatMap((e) => e.passos));
  let achouAtual = false;
  return ETAPAS.map((e, i) => {
    const deles = passos.filter((p) => e.passos.includes(p.id) || (i === ETAPAS.length - 1 && !conhecidos.has(p.id)));
    const concluida = deles.every((p) => p.concluido);
    const estado: EtapaDoGuia["estado"] = concluida ? "feita" : achouAtual ? "depois" : "agora";
    if (!concluida) achouAtual = true;
    return { id: e.id, titulo: e.titulo, passos: deles, concluida, estado };
  });
}

const ICONE_DA_ETAPA: Record<EtapaDoGuia["id"], { icone: ReactNode; tom: TomDoLadrilho }> = {
  ligar: { icone: <Smartphone size={19} />, tom: "hugi" },
  quando: { icone: <Clock size={19} />, tom: "ia" },
  testar: { icone: <MessageCircleReply size={19} />, tom: "wa" },
};

const SELO_DA_ETAPA = {
  feita: { tom: "sucesso", rotulo: "Feito" },
  agora: { tom: "info", rotulo: "Agora" },
  depois: { tom: "neutro", rotulo: "Depois" },
} as const;

function Guia(props: { readonly etapas: readonly EtapaDoGuia[] }) {
  const atual = props.etapas.find((e) => e.estado === "agora") ?? props.etapas[props.etapas.length - 1]!;
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const cartoes: CartaoDaGrade[] = props.etapas.map((e, i) => ({
    id: e.id,
    titulo: `${i + 1}. ${e.titulo}`,
    rotuloCurto: e.titulo,
    contagem: <Selo tom={SELO_DA_ETAPA[e.estado].tom}>{SELO_DA_ETAPA[e.estado].rotulo}</Selo>,
    classe: `hugi-inicio-etapa is-${e.estado}`,
    conteudo: <Etapa etapa={e} ligada={props.etapas[0]!.concluida} />,
  }));
  return (
    <GradeDeCartoes
      rotulo="Ativação"
      cartoes={cartoes}
      ativo={escolhida ?? atual.id}
      aoTrocar={setEscolhida}
      colunas="repeat(3, minmax(0, 1fr))"
    />
  );
}

function Etapa(props: { readonly etapa: EtapaDoGuia; readonly ligada: boolean }) {
  const { etapa: e } = props;
  const visual = ICONE_DA_ETAPA[e.id];
  return (
    <div className="hugi-inicio-etapa-miolo">
      <Ladrilho tom={visual.tom} grande>{visual.icone}</Ladrilho>
      {/* No Testar, o QR vem PRIMEIRO: é o que a pessoa usa; a lista só confirma. */}
      {e.id === "testar" ? <Testar etapa={e} ligada={props.ligada} /> : null}
      <ul className="hugi-inicio-subpassos">
        {e.passos.map((p) => (
          <li key={p.id} className={p.concluido ? "is-concluido" : undefined}>
            <span aria-hidden="true" className="hugi-inicio-passo-marca">{p.concluido ? "✓" : ""}</span>
            <span className="hugi-inicio-passo-texto">
              <span className="hugi-inicio-passo-rotulo">{p.rotulo}</span>
              {p.detalhe ? <span className="hugi-inicio-passo-detalhe">{p.detalhe}</span> : null}
              {!p.concluido && p.dependeDeTerceiro ? <Selo tom="atencao">depende da Meta</Selo> : null}
            </span>
          </li>
        ))}
      </ul>
      {e.id === "ligar" ? <AcaoDeLigar etapa={e} /> : null}
      {e.id === "quando" ? <AcaoDeQuando etapa={e} /> : null}
    </div>
  );
}

/** O botão de uma etapa: só a etapa ATUAL tem o primário (uma ação primária por tela). */
function BotaoDaEtapa(props: { readonly href: string; readonly primario: boolean; readonly children: ReactNode }) {
  return (
    <Link href={props.href} className={`v2-botao${props.primario ? " is-primario" : ""}`}>
      {props.children}
    </Link>
  );
}

function AcaoDeLigar(props: { readonly etapa: EtapaDoGuia }) {
  if (props.etapa.concluida) return null;
  return (
    <div className="hugi-inicio-etapa-acao">
      {/* O Profissional vai direto à API Hugi; o oficial fica num link (spec 23, jornada 1). */}
      <BotaoDaEtapa href="/canais/conectar?canal=whatsapp_qr" primario={props.etapa.estado === "agora"}>
        <QrCode size={14} aria-hidden="true" /> Ligar o meu número
      </BotaoDaEtapa>
      <Link href="/canais/conectar?canal=whatsapp_oficial" className="v2-link">Prefere a API da Meta?</Link>
    </div>
  );
}

/**
 * O assistente principal: do resumo (C2) quando o servidor o manda; senão, da lista de assistentes — a mesma chave
 * de consulta de `useAgentes`, e só pedida quando o resumo não trouxe.
 */
function usePrincipal(): string | null {
  const resumo = useResumoDoInicio();
  const r = (resumo.data ?? null) as ResumoComContratos | null;
  const doResumo = r?.assistentes?.principal?.id ?? null;
  const lista = useQuery({
    queryKey: ["agentes"],
    queryFn: () => chamar<{ agentes: ResumoDoAgente[] }>("/api/painel/agentes"),
    enabled: resumo.isFetched && doResumo === null,
  });
  if (doResumo) return doResumo;
  const agentes = lista.data?.agentes ?? [];
  return (agentes.find((a) => a.principal) ?? agentes[0])?.id ?? null;
}

function AcaoDeQuando(props: { readonly etapa: EtapaDoGuia }) {
  const principal = usePrincipal();
  if (props.etapa.concluida) return null;
  const falta = (id: string) => props.etapa.passos.some((p) => p.id === id && !p.concluido);
  // Escolher o horário vem antes de publicar: publicar com "nunca" dá sucesso e fica mudo (spec 23).
  const aba = falta("quando") || !falta("publicar") ? "quando" : "publicar";
  const href = principal ? `/automacoes/assistente/${encodeURIComponent(principal)}?aba=${aba}` : "/automacoes";
  return (
    <div className="hugi-inicio-etapa-acao">
      <BotaoDaEtapa href={href} primario={props.etapa.estado === "agora"}>
        <Clock size={14} aria-hidden="true" /> {aba === "quando" ? "Escolher quando ele responde" : "Publicar o assistente"}
      </BotaoDaEtapa>
    </div>
  );
}

/** O número para testar: o primeiro WhatsApp vivo, preferindo o confirmado. */
export function numeroParaTestar(conexoes: readonly Conexao[]): Conexao | null {
  const vivas = conexoes.filter((c) => c.canal === "whatsapp" && c.estado !== "revogada" && c.estado !== "desconectada" && identificadorLegivel(c.numeroExibicao ?? "") !== "");
  return vivas.find((c) => c.confirmadaEm !== null) ?? vivas[0] ?? null;
}

/** `wa.me` só aceita dígitos. */
export function linkDoWhatsApp(numero: string): string {
  return `https://wa.me/${numero.replace(/\D/g, "")}?text=${encodeURIComponent("Oi")}`;
}

/**
 * TESTAR — o QR e o `wa.me` do número, e o estado AO VIVO (esperando → respondeu).
 *
 * Enquanto esta é a etapa atual, a ativação é relida a cada 5 s: a pessoa manda o "oi" do outro celular e vê o passo
 * virar sem recarregar a tela. O estado é o do servidor (`conversa`: chegou e foi respondida na mesma conversa).
 */
function Testar(props: { readonly etapa: EtapaDoGuia; readonly ligada: boolean }) {
  const cliente = useQueryClient();
  const conexoes = useConexoes();
  const conversa = props.etapa.passos.find((p) => p.id === "conversa") ?? null;
  const alerta = props.etapa.passos.find((p) => p.id === "alerta") ?? null;
  const numero = numeroParaTestar(conexoes.data?.conexoes ?? []);
  // Pela máscara, como todo número (a catraca `npm run identificador`); o `wa.me` tira o que não for dígito.
  const legivel = numero ? identificadorLegivel(numero.numeroExibicao ?? "") : "";
  const link = legivel ? linkDoWhatsApp(legivel) : null;
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    if (props.etapa.estado !== "agora") return;
    const t = setInterval(() => void cliente.invalidateQueries({ queryKey: ["ativacao"] }), 5_000);
    return () => clearInterval(t);
  }, [props.etapa.estado, cliente]);

  useEffect(() => {
    if (!link) return;
    let vivo = true;
    void import("qrcode")
      .then((m) => m.toDataURL(link, { margin: 1, width: 132 }))
      .then((url) => { if (vivo) setQr(url); })
      .catch(() => { /* sem QR, o link continua — ele é o mesmo destino */ });
    return () => { vivo = false; };
  }, [link]);

  if (!props.ligada || !numero || !link) {
    return <p className="hugi-inicio-etapa-nota">Primeiro ligue o número.</p>;
  }

  return (
    <div className="hugi-inicio-teste">
      <div className="hugi-inicio-teste-qr">
        {qr ? <img src={qr} alt={`QR para mandar um oi a ${legivel}`} width={132} height={132} /> : <QrCode size={48} aria-hidden="true" />}
      </div>
      <div className="hugi-inicio-teste-texto">
        <strong>{legivel}</strong>
        <span className="hugi-inicio-passo-detalhe">Mande um oi de outro celular.</span>
        <a href={link} target="_blank" rel="noreferrer" className="v2-link">Abrir no WhatsApp</a>
        <span role="status" className="hugi-inicio-teste-estado">
          {conversa?.concluido ? (
            <Selo tom="sucesso">Respondeu ✓</Selo>
          ) : (
            <Selo tom="info">Esperando</Selo>
          )}
        </span>
        {alerta && !alerta.concluido && /ajustes/i.test(alerta.detalhe ?? "") ? (
          <Link href="/ajustes?aba=gerais" className="v2-link">Pôr o número pessoal</Link>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// A checklist geral (equipe) — continua lista
// ---------------------------------------------------------------------------------------------

function Checklist(props: { readonly passos: readonly PassoDaAtivacao[] }) {
  // O primeiro pendente é o DESTAQUE. §4: "com um passo destacado por vez".
  const proximo = props.passos.find((p) => !p.concluido);

  const lista = (
    <ol className="hugi-inicio-passos">
      {props.passos.map((p, i) => {
        const emDestaque = p.id === proximo?.id;
        return (
          <li
            key={p.id}
            // Concluído vira "linha discreta" (§4), e não desaparece. O tom apagado é o token, não opacidade.
            className={`hugi-inicio-passo${emDestaque ? " is-destaque" : ""}${p.concluido ? " is-concluido" : ""}`}
          >
            <span aria-hidden="true" className="hugi-inicio-passo-marca">
              {p.concluido ? "✓" : i + 1}
            </span>

            <span className="hugi-inicio-passo-texto">
              <span className="hugi-inicio-passo-rotulo">{p.rotulo}</span>
              {p.detalhe ? <span className="hugi-inicio-passo-detalhe">{p.detalhe}</span> : null}
              {/* A espera que não é do cliente é DITA: ninguém aqui acelera a aprovação da Meta. */}
              {!p.concluido && p.dependeDeTerceiro ? <Selo tom="atencao">depende da Meta</Selo> : null}
            </span>

            {emDestaque ? <AcaoDoPasso id={p.id} /> : null}
          </li>
        );
      })}
    </ol>
  );

  return <GradeDeCartoes rotulo="Ativação" cartoes={[{ id: "passos", titulo: "Passos da ativação", conteudo: lista }]} />;
}

/** O botão de um passo: um botão dentro do link, para o clique e o teclado levarem ao mesmo lugar. */
function BotaoDoPasso(props: { readonly href: string; readonly primario?: boolean; readonly children: ReactNode }) {
  return (
    <Link href={props.href}>
      <button type="button" className={`v2-botao${props.primario ? " is-primario" : ""}`}>
        {props.children}
      </button>
    </Link>
  );
}

/**
 * O botão de cada passo da checklist geral. Os do Profissional moram no guia, cada um levando à aba certa do
 * assistente — e não mais à lista genérica de Automações (spec 23, `Inicio.tsx:222-227` de antes).
 */
function AcaoDoPasso(props: { readonly id: string }) {
  switch (props.id) {
    case "canal":
      // "Conectar", e não "Ver canais": a lista de canais, neste ponto, está sempre vazia.
      return <BotaoDoPasso href="/canais/conectar" primario>Conectar um canal</BotaoDoPasso>;
    case "filas":
      return <BotaoDoPasso href="/ajustes?aba=filas">Criar fila</BotaoDoPasso>;
    case "equipe":
      // Convidar funciona desde 16/08 (H9b). O passo fecha com gente em `fila_atendentes`, que se põe na Equipe (H69).
      return <BotaoDoPasso href="/equipe">Abrir Equipe e acessos</BotaoDoPasso>;
    case "template":
      // O rótulo diz o que o clique FAZ: navegar. Daqui não dá para saber qual número sincronizar.
      return <BotaoDoPasso href="/canais">Ver templates em Canais</BotaoDoPasso>;
    case "primeira_resposta":
      return (
        <button
          type="button"
          className="v2-botao"
          onClick={() => {
            // O inbox é OUTRO build, na mesma origem (§3 da spec 02) — navegação de verdade.
            window.location.href = "/atender";
          }}
        >
          Abrir o atendimento
        </button>
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------------------------
// A central de ações
// ---------------------------------------------------------------------------------------------

/**
 * A central — a faixa de estado, quatro números que levam a algum lugar, o que precisa de você e a equipe agora.
 *
 * O bloco "Precisam de você" nasceu de um caso concreto: um número ficou 28 horas fora do ar com o `/health`
 * dizendo "todos no ar" (03/09), e o dono só saberia abrindo Canais. Desde a spec 23 cada linha traz o verbo que
 * resolve — o dono não precisa adivinhar para onde ir.
 */
function PainelDeOperacao() {
  const sessao = useSessao();
  const pode = sessao.permissoes.includes("read_report");
  const volume = useRelatorioDeVolume(7);
  const resumo = useResumoDoInicio();
  const conexoes = useConexoes();
  // Sem o resumo (falha, ou ainda carregando) o bloco de atenção não aparece — a tela continua de pé.
  const r = (resumo.data ?? null) as ResumoComContratos | null;
  const itens = itensDeAtencao(r, new Date(), conexoes.data?.conexoes ?? []);

  if (!pode) {
    return (
      <>
        <FaixaDeEstado itens={itens} carregado={resumo.isFetched} />
        <SemPermissao>
          Os números da operação exigem a permissão <code>read_report</code>. Um administrador da sua
          empresa concede.
        </SemPermissao>
        <Cartoes itens={itens} />
      </>
    );
  }

  if (volume.isPending) return <Carregando />;
  if (volume.error) {
    return (
      <EstadoDaFalha
        falha={falhaDe(volume.error) ?? { tipo: "inesperado", erro: { erro: "?" }, status: 0 }}
        aoRepetir={() => void volume.refetch()}
      />
    );
  }

  return (
    <>
      <FaixaDeEstado itens={itens} carregado={resumo.isFetched} />
      <MetricasDoDia volume={volume.data!} resumo={r} />
      <Cartoes itens={itens} />
    </>
  );
}

/** "Tudo no ar" ou "2 coisas precisam de você" — conta LINHAS (um número com duas pendências é uma coisa). */
export function fraseDoEstado(itens: readonly ItemDeAtencao[]): { readonly ok: boolean; readonly texto: string } {
  const n = agruparAtencao(itens).length;
  if (n === 0) return { ok: true, texto: "Tudo no ar" };
  return { ok: false, texto: n === 1 ? "1 coisa precisa de você" : `${n} coisas precisam de você` };
}

function FaixaDeEstado(props: { readonly itens: readonly ItemDeAtencao[]; readonly carregado: boolean }) {
  // Antes do resumo chegar, nada: dizer "Tudo no ar" sem ter olhado seria o defeito de 03/09 ao contrário.
  if (!props.carregado) return null;
  const e = fraseDoEstado(props.itens);
  return (
    <div role="status" className={`hugi-inicio-estado ${e.ok ? "is-ok" : "is-atencao"}`}>
      {e.ok ? <CircleCheck size={16} aria-hidden="true" /> : <TriangleAlert size={16} aria-hidden="true" />}
      <span>{e.texto}</span>
    </div>
  );
}

/** Acima disto o p90 da primeira resposta sai em vermelho: uma em dez conversas esperou mais que isto. */
const P90_QUE_PEDE_ATENCAO_S = 3600;

/**
 * QUATRO números, cada um levando ao lugar onde ele se resolve (spec 23, jornada 4): Esperando → o atendimento;
 * 1ª resposta → o relatório de tempos; Sem resposta hoje → o backlog; Consumo do mês → o plano.
 */
function MetricasDoDia(props: { readonly volume: NonNullable<ReturnType<typeof useRelatorioDeVolume>["data"]>; readonly resumo: ResumoComContratos | null }) {
  const [, navegar] = useLocation();
  const d = props.volume;
  const r = props.resumo;
  const p90 = d.primeiraResposta.p90Segundos;
  const p90Lento = p90 !== null && p90 >= P90_QUE_PEDE_ATENCAO_S;
  const semRespostaHoje = r?.hoje ? Math.max(0, r.hoje.recebidas - r.hoje.respondidas) : null;
  const consumo = useConsumoDoMes(r);

  const metricas: Metrica[] = [
    {
      id: "esperando",
      icone: <Hourglass />,
      tom: "ag",
      rotulo: "Esperando",
      valor: d.aguardandoAgora,
      detalhe: notaDaEspera(d.aguardandoAgora, r?.esperando ?? null),
      // "Esperando" acima de zero pede ação. Realçar todos seria não realçar nenhum.
      ...(d.aguardandoAgora > 0 ? { alerta: "atencao" as const } : {}),
      // O inbox é OUTRO build: o link é navegação de verdade.
      href: "/atender?estado=aguardando",
    },
    {
      id: "primeira-resposta",
      icone: <Timer />,
      tom: "ia",
      rotulo: "1ª resposta",
      dica: "Mediana dos últimos 7 dias, não média: um atendimento esquecido por 16 horas move a média de todos e esconde que 90% foram respondidos em minutos.",
      valor: duracaoCurta(d.primeiraResposta.medianaSegundos),
      detalhe: <span className={p90Lento ? "hugi-inicio-alerta" : undefined}>p90 {duracaoCurta(p90)}</span>,
      aoAbrir: () => navegar("/relatorios?aba=volume"),
    },
    {
      id: "sem-resposta-hoje",
      icone: <MessageSquareWarning />,
      tom: "rl",
      rotulo: "Sem resposta hoje",
      valor: semRespostaHoje,
      detalhe: r?.hoje ? `de ${contar(r.hoje.recebidas, "recebida", "recebidas")}` : undefined,
      ...(semRespostaHoje !== null && semRespostaHoje > 0 ? { alerta: "atencao" as const } : {}),
      aoAbrir: () => navegar("/relatorios?aba=backlog"),
    },
    {
      id: "consumo",
      icone: <Gauge />,
      tom: "cf",
      rotulo: "Consumo do mês",
      valor: consumo ? consumo.usadas.toLocaleString("pt-BR") : null,
      detalhe: consumo
        ? consumo.franquia !== null
          ? `de ${consumo.franquia.toLocaleString("pt-BR")} com o assistente`
          : "conversas com o assistente"
        : undefined,
      ...(consumo?.alerta ? { alerta: consumo.alerta } : {}),
      aoAbrir: () => navegar("/ajustes?aba=plano"),
    },
  ];

  return <Metricas rotulo="A operação" itens={metricas} />;
}

/**
 * O consumo de IA do mês: do resumo (C2) quando vem; senão, do próprio plano (a mesma chave de `useConsumoDoPlano`),
 * pedido só quando o resumo chegou sem o campo.
 */
function useConsumoDoMes(r: ResumoComContratos | null): { usadas: number; franquia: number | null; alerta: "atencao" | "perigo" | null } | null {
  const semNoResumo = r !== null && r.plano === undefined;
  const plano = useQuery({
    queryKey: ["plano"],
    queryFn: () => chamar<ConsumoDoPlano>("/api/painel/plano"),
    enabled: semNoResumo,
    retry: false,
  });
  if (r?.plano && typeof r.plano.usadas === "number") {
    const p = r.plano;
    return { usadas: p.usadas, franquia: p.franquia, alerta: p.agenteParado || p.situacaoIa === "estourou" ? "perigo" : p.situacaoIa === "perto" ? "atencao" : null };
  }
  if (semNoResumo && typeof plano.data?.conversasIa === "number") {
    const p = plano.data;
    return { usadas: p.conversasIa, franquia: p.franquiaIa ?? null, alerta: p.agenteParado || p.situacaoIa === "estourou" ? "perigo" : p.situacaoIa === "perto" ? "atencao" : null };
  }
  return null;
}

function notaDaEspera(agora: number, esperando: ResumoDoInicio["esperando"]): ReactNode {
  if (agora === 0) return "fila vazia";
  if (esperando && esperando.haMaisDeMin > 0) {
    return (
      <span className="font-medium text-[color:var(--erro-texto)]">
        {esperando.haMaisDeMin === agora ? "todas" : String(esperando.haMaisDeMin)} há mais de {esperando.limiteMin} min
      </span>
    );
  }
  return "na fila, sem ninguém";
}

/** A grade: "Precisam de você" (só quando há algo) e "Equipe agora". */
function Cartoes(props: { readonly itens: readonly ItemDeAtencao[] }) {
  // No celular (um cartão por vez) abre no primeiro — "Precisam de você" quando ele existe.
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const grupos = agruparAtencao(props.itens);
  const cartoes: CartaoDaGrade[] = [];
  if (grupos.length > 0) {
    cartoes.push({
      id: "atencao",
      titulo: "Precisam de você",
      rotuloCurto: "Você",
      contagem: grupos.length,
      conteudo: <PrecisamDeVoce grupos={grupos} />,
    });
  }
  cartoes.push({
    id: "equipe",
    titulo: "Equipe agora",
    rotuloCurto: "Equipe",
    conteudo: <EquipeAgora />,
  });
  return (
    <GradeDeCartoes
      rotulo="Painel de hoje"
      cartoes={cartoes}
      ativo={escolhido ?? cartoes[0]!.id}
      aoTrocar={setEscolhido}
      {...(cartoes.length > 1 ? { colunas: "minmax(0, 7fr) minmax(0, 5fr)" } : {})}
    />
  );
}

/** O verbo que resolve uma linha. `pausar` vira o botão com confirmação; sem nada a pausar, cai no link. */
export interface VerboDoItem {
  readonly rotulo: string;
  /** Navegação de verdade (outro build), ou rota do painel. Um dos dois. */
  readonly href?: string;
  readonly rota?: string;
  readonly pausar?: { readonly conexaoId: string };
}

export interface ItemDeAtencao {
  readonly chave: string;
  /** Linhas do mesmo número se juntam (`numero:<id>`); as outras têm grupo próprio. */
  readonly grupo: string;
  readonly texto: string;
  readonly detalhe: string;
  readonly tom: "ruim" | "degradado";
  readonly rotulo: string;
  readonly verbo: VerboDoItem;
}

const MOTIVO_DA_QUEDA: Readonly<Record<string, string>> = {
  socket_caido: "fora do ar",
  processo_sumiu: "fora do ar",
  transporte_parado: "fora do ar",
};

/**
 * O que precisa de alguém, a partir do resumo — pura, para o teste dizer o que entra e o que não entra. A ordem é a
 * do prejuízo: número fora do ar cala tudo que depende dele; depois a saúde (o que ninguém percebe até o bloqueio);
 * o assistente que recusa por não estar publicado; a fila (gente esperando); o plano e o chamado; e por fim
 * publicação e campanha, que já estão gravadas e esperam uma ação.
 *
 * `conexoes` diz o canal de cada número, para "Reconectar" abrir o canal CERTO; sem ela, abre o passo de conectar.
 */
export function itensDeAtencao(
  r: ResumoComContratos | ResumoDoInicio | null,
  agora: Date = new Date(),
  conexoes: readonly Pick<Conexao, "id" | "canal" | "modo">[] = [],
): ItemDeAtencao[] {
  if (!r) return [];
  const rc = r as ResumoComContratos;
  const itens: ItemDeAtencao[] = [];

  for (const c of r.canais?.foraDoAr ?? []) {
    // O rótulo vem do `COALESCE(apelido, numero_exibicao, endereco, id)` do servidor; a máscara é idempotente e
    // deixa apelido passar — é o que faz as linhas deste cartão pararem de se contradizer.
    const rotulo = identificadorLegivel(c.rotulo);
    const grupo = `numero:${c.id}`;
    if (c.motivo === "sem_confirmacao") {
      itens.push({ chave: `canal:${c.id}`, grupo, texto: rotulo, detalhe: "esperando você confirmar que o número é seu", tom: "degradado", rotulo: "Sem confirmação", verbo: { rotulo: "Confirmar", rota: "/canais" } });
    } else if (c.motivo === "degradada") {
      itens.push({ chave: `canal:${c.id}`, grupo, texto: rotulo, detalhe: "com problema do lado do canal", tom: "ruim", rotulo: "Degradada", verbo: { rotulo: "Ver canal", rota: "/canais" } });
    } else {
      const ha = c.desde ? haQuantoTempo(Math.max(0, Math.round((agora.getTime() - new Date(c.desde).getTime()) / 1000))) : null;
      const conexao = conexoes.find((x) => x.id === c.id);
      itens.push({
        chave: `canal:${c.id}`, grupo, texto: rotulo,
        detalhe: `${MOTIVO_DA_QUEDA[c.motivo] ?? "fora do ar"}${ha ? ` há ${ha}` : ""}`,
        tom: "ruim", rotulo: "Fora do ar",
        verbo: { rotulo: "Reconectar", rota: canalDeConectar(conexao?.canal, conexao?.modo) },
      });
    }
  }

  /*
   * SAÚDE DO NÚMERO (spec 19). Número indo mal ainda funciona, mas é o que ninguém percebe até o bloqueio chegar.
   * O verbo é PAUSAR o que sai por ele (spec 23) — com confirmação, e só para quem tem a permissão da rota.
   */
  for (const s of r.canais?.saude ?? []) {
    const rota = `/canais/saude/${s.conexaoId}`;
    const rotulo = identificadorLegivel(s.rotulo);
    const grupo = `numero:${s.conexaoId}`;
    const pausar = { rotulo: "Pausar campanhas deste número", rota, pausar: { conexaoId: s.conexaoId } };
    if (s.motivo === "punida") {
      const p = s.punicoes[0] ?? "";
      // O SELO nomeia a punição (a palavra vem da tela de saúde, para as duas dizerem a mesma coisa).
      itens.push({ chave: `saude:${s.conexaoId}`, grupo, texto: rotulo, detalhe: fraseDaPunicao(p), tom: "ruim", rotulo: nomeDaPunicao(p), verbo: pausar });
    } else if (s.motivo === "risco") {
      itens.push({
        chave: `saude:${s.conexaoId}`, grupo, texto: rotulo,
        detalhe: `saúde em risco${s.indice === null ? "" : ` (índice ${s.indice})`}`,
        tom: "ruim", rotulo: "Saúde", verbo: pausar,
      });
    } else {
      // `variacao` é negativa aqui por construção — o aviso só nasce com queda.
      itens.push({
        chave: `saude:${s.conexaoId}`, grupo, texto: rotulo,
        detalhe: `a saúde caiu ${Math.abs(s.variacao ?? 0)} pontos no período`,
        tom: "degradado", rotulo: "Caindo", verbo: { rotulo: "Ver saúde", rota },
      });
    }
  }

  // O ASSISTENTE QUE RECUSA (C2): "não está publicado neste número" — a raiz das 142 respostas perdidas.
  for (const a of rc.assistentes?.semPublicacao ?? []) {
    if (a.recusas7d <= 0) continue;
    itens.push({
      chave: `assistente:${a.id}`, grupo: `assistente:${a.id}`, texto: a.nome,
      detalhe: `${contar(a.recusas7d, "resposta perdida", "respostas perdidas")} em 7 dias`,
      tom: "ruim", rotulo: "Sem publicação",
      verbo: { rotulo: "Publicar", rota: `/automacoes/assistente/${encodeURIComponent(a.id)}?aba=publicar` },
    });
  }

  if (r.esperando && r.esperando.haMaisDeMin > 0) {
    const e = rc.esperando!;
    itens.push({
      chave: "fila", grupo: "fila",
      texto: `${contar(e.haMaisDeMin, "conversa espera", "conversas esperam")} há mais de ${e.limiteMin} min`,
      detalhe: e.maisAntigaSegundos !== null ? `a mais antiga há ${haQuantoTempo(e.maisAntigaSegundos)}` : "",
      tom: "ruim",
      rotulo: "Na fila",
      verbo: { rotulo: "Atender a mais antiga", href: e.maisAntigaId ? `/atender/${encodeURIComponent(e.maisAntigaId)}` : "/atender" },
    });
  }

  // A FRANQUIA DE IA (C2): perto do fim, ou o assistente já parado por ela.
  const p = rc.plano;
  if (p && (p.agenteParado || p.situacaoIa === "estourou" || p.situacaoIa === "perto")) {
    itens.push({
      chave: "plano", grupo: "plano", texto: "Franquia do assistente",
      detalhe: p.agenteParado
        ? "acabou, e o assistente parou de responder"
        : p.franquia !== null
          ? `${p.usadas.toLocaleString("pt-BR")} de ${p.franquia.toLocaleString("pt-BR")} conversas no mês`
          : `${p.usadas.toLocaleString("pt-BR")} conversas no mês`,
      tom: p.agenteParado || p.situacaoIa === "estourou" ? "ruim" : "degradado",
      rotulo: p.agenteParado ? "Parado" : p.situacaoIa === "estourou" ? "Estourou" : "Perto do fim",
      verbo: { rotulo: "Ver plano", rota: "/ajustes?aba=plano" },
    });
  }

  if ((rc.chamadosAguardando ?? 0) > 0) {
    const n = rc.chamadosAguardando!;
    itens.push({
      chave: "chamados", grupo: "chamados",
      texto: contar(n, "chamado espera você", "chamados esperam você"),
      detalhe: "o suporte respondeu",
      tom: "degradado", rotulo: "Chamado",
      verbo: { rotulo: "Responder", rota: "/chamados" },
    });
  }

  for (const pub of r.publicacoes?.precisamDeAtencao ?? []) {
    const detalhe =
      pub.estado === "freada" ? "pausada pelo freio" : pub.estado === "aguardando_conexao" ? "esperando a conexão" : pub.estado === "falhou" ? "falhou" : contar(pub.falhados, "grupo falhou", "grupos falharam");
    itens.push({ chave: `pub:${pub.id}`, grupo: `pub:${pub.id}`, texto: pub.nome, detalhe, tom: pub.estado === "falhou" ? "ruim" : "degradado", rotulo: "Publicação", verbo: { rotulo: "Abrir", rota: `/publicacoes/${pub.id}` } });
  }

  for (const k of r.campanhas?.pausadas ?? []) {
    itens.push({ chave: `camp:${k.id}`, grupo: `camp:${k.id}`, texto: k.nome, detalhe: `pausada${k.motivo ? ` · ${k.motivo}` : ""}`, tom: "degradado", rotulo: "Campanha", verbo: { rotulo: "Abrir", rota: `/campanhas?campanha=${k.id}` } });
  }

  return itens;
}

export interface GrupoDeAtencao {
  readonly grupo: string;
  readonly texto: string;
  readonly itens: readonly ItemDeAtencao[];
  /** Um verbo por rótulo: o número fora do ar e em risco tem "Reconectar" e "Pausar", não dois "Pausar". */
  readonly verbos: readonly VerboDoItem[];
}

/** Junta as linhas do mesmo número numa só, na ordem em que o primeiro item dele apareceu. */
export function agruparAtencao(itens: readonly ItemDeAtencao[]): GrupoDeAtencao[] {
  const mapa = new Map<string, { texto: string; itens: ItemDeAtencao[] }>();
  for (const i of itens) {
    const g = mapa.get(i.grupo);
    if (g) g.itens.push(i);
    else mapa.set(i.grupo, { texto: i.texto, itens: [i] });
  }
  return [...mapa.entries()].map(([grupo, g]) => {
    const verbos: VerboDoItem[] = [];
    for (const i of g.itens) if (!verbos.some((v) => v.rotulo === i.verbo.rotulo)) verbos.push(i.verbo);
    return { grupo, texto: g.texto, itens: g.itens, verbos };
  });
}

/**
 * "há 48 min", "há 2h 10min", "há 17 dias". O `duracaoCurta` para em horas — serve para mediana e
 * p90, e não para uma queda de duas semanas, que sairia como "430h 20min" (medido no banco de dev).
 */
function haQuantoTempo(segundos: number): string {
  if (segundos < 86_400) return duracaoCurta(segundos);
  return contar(Math.floor(segundos / 86_400), "dia", "dias");
}

const TOM_DO_ITEM = { ruim: "perigo", degradado: "atencao" } as const;

function PrecisamDeVoce(props: { readonly grupos: readonly GrupoDeAtencao[] }) {
  return (
    <ul className="hugi-inicio-atencao">
      {props.grupos.map((g) => (
        <li key={g.grupo}>
          <span className="hugi-inicio-atencao-texto">
            <strong className="hugi-inicio-atencao-alvo">{g.texto}</strong>
            <span className="hugi-inicio-atencao-sinais">
              {g.itens.map((i) => (
                <span key={i.chave} className="hugi-inicio-atencao-sinal">
                  <Selo tom={TOM_DO_ITEM[i.tom]}>{i.rotulo}</Selo>
                  {i.detalhe ? <span className="hugi-inicio-atencao-detalhe">{i.detalhe}</span> : null}
                </span>
              ))}
            </span>
          </span>
          <span className="hugi-inicio-atencao-verbos">
            {g.verbos.map((v) => <BotaoDoVerbo key={v.rotulo} verbo={v} />)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function BotaoDoVerbo(props: { readonly verbo: VerboDoItem }) {
  const v = props.verbo;
  const link = v.href ? (
    <a href={v.href} className="v2-botao is-pequeno">{v.rotulo}</a>
  ) : (
    <Link href={v.rota ?? "/"} className="v2-botao is-pequeno">{v.rotulo}</Link>
  );
  if (!v.pausar) return link;
  // Sem permissão ou sem nada saindo pelo número, pausar não faz nada: o verbo vira "Ver saúde".
  const verSaude = <Link href={v.rota ?? "/canais/saude"} className="v2-botao is-pequeno">Ver saúde</Link>;
  return <PausarDoNumero conexaoId={v.pausar.conexaoId} rotulo={v.rotulo} senao={verSaude} />;
}

// ---------------------------------------------------------------------------------------------
// Equipe agora (D5)
// ---------------------------------------------------------------------------------------------

/**
 * A presença do time (contrato C5): quem está com a tela do atendimento aberta e VISÍVEL agora. Sem histórico e sem
 * "ocioso há" (H32). Sem a rota (servidor mais velho), a coluna de presença simplesmente não aparece.
 */
function usePresenca() {
  return useQuery({
    queryKey: ["equipe", "presenca"],
    queryFn: () => chamar<{ online: string[] }>("/api/painel/equipe/presenca"),
    refetchInterval: 30_000,
    retry: false,
  });
}

export interface PessoaAgora {
  readonly id: string;
  readonly nome: string;
  readonly abertos: number;
  readonly online: boolean | null;
  readonly semFila: boolean;
  readonly foraDaJornada: boolean;
}

/** Quem está com quanto — a carga primeiro, e entre iguais quem está online. Pura, para o teste. */
export function pessoasAgora(
  itens: readonly AtendenteNaLista[],
  horario: readonly (string | null)[] | null,
  fuso: string,
  online: readonly string[] | null,
  agora: Date = new Date(),
): PessoaAgora[] {
  return itens
    .filter((a) => a.ativo)
    .map((a) => ({
      id: a.atendenteId,
      nome: a.nome ?? "sem nome ainda",
      abertos: a.atendimentosAbertos,
      online: online ? online.includes(a.atendenteId) : null,
      semFila: a.filas.length === 0,
      foraDaJornada: estadoDaJornada(a, horario, fuso, agora) === "fora",
    }))
    .sort((x, y) => y.abertos - x.abertos || Number(y.online === true) - Number(x.online === true) || x.nome.localeCompare(y.nome));
}

function EquipeAgora() {
  const equipe = useEquipe();
  const presenca = usePresenca();
  const [, navegar] = useLocation();

  if (equipe.isPending) return <Carregando />;
  if (equipe.error || !equipe.data) {
    return <p className="hugi-inicio-etapa-nota">A equipe não carregou.</p>;
  }
  const d = equipe.data;
  const pessoas = pessoasAgora(d.itens, d.horarioDoTenant, d.fuso, presenca.data?.online ?? null);
  if (pessoas.length === 0) {
    return (
      <div className="hugi-inicio-equipe-vazia">
        <span>Ninguém na equipe ainda.</span>
        <Link href="/equipe" className="v2-link">Convidar</Link>
      </div>
    );
  }
  return (
    <ul className="hugi-inicio-equipe">
      {pessoas.map((p) => (
        <li key={p.id}>
          {/* Pôr em fila continua na Equipe (decisão do owner, 01/11): o clique leva à pessoa lá. */}
          <button type="button" className="hugi-inicio-pessoa" onClick={() => navegar(`/equipe?pessoa=${encodeURIComponent(p.id)}`)}>
            {p.online === null ? null : (
              <span className={`hugi-inicio-presenca${p.online ? " is-online" : ""}`} role="img" aria-label={p.online ? "online" : "offline"} />
            )}
            <span className="hugi-inicio-pessoa-nome">{p.nome}</span>
            <span className="hugi-inicio-pessoa-sinais">
              {p.semFila ? <Selo tom="atencao">sem fila</Selo> : null}
              {p.foraDaJornada ? <Selo tom="neutro">fora da jornada</Selo> : null}
            </span>
            <span className="hugi-inicio-pessoa-carga">{contar(p.abertos, "aberto", "abertos")}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
